import React, { useMemo } from 'react'
import { View, StyleSheet, Text, TouchableOpacity } from 'react-native'
import { sections } from '../sections'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import ProjectStore from '../Store'
import {
  IconCheckBlack,
  ChevronRightDark,
  IconCheckBlue,
  IconCloseSquare,
  IconCheckGreen2,
} from '../../../assets/icons'
import { useResponsive } from '../../../utils/hooks'
import { Categories, ProjectSections } from '@linqpal/models/src/dictionaries'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { paths } from '../../links'

const sectionColors = {
  current: {
    checkIcon: <IconCheckBlue />,
    backgroundColor: 'rgba(219, 242, 255, 1)',
  },
  pending: {
    checkIcon: <IconCheckBlack />,
    backgroundColor: 'rgba(248, 249, 249, 1)',
  },
  filled: {
    checkIcon: <IconCheckGreen2 />,
    backgroundColor: 'rgba(14, 192, 107, 0.1)',
  },
}

const SectionName: React.FC<{ name: string; category: Categories }> = ({
  name,
  category,
}) => {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <View
        style={composeStyle(SectionNameStyles.checkContainer, {
          backgroundColor: sectionColors[category].backgroundColor,
        })}
      >
        {sectionColors[category].checkIcon}
      </View>
      <Text style={SectionNameStyles.name}>{name}</Text>
    </View>
  )
}

export const ProgressHeader: React.FC = () => {
  const { screenWidth, sm } = useResponsive()
  const { t } = useTranslation('global')
  const navigation = useNavigation()
  const { progress, sectionCategory } = ProjectStore
  const sectionList = Object.keys(sections) as [ProjectSections]
  const progressWidth = useMemo(
    () => screenWidth * progress,
    [screenWidth, progress],
  )
  const onClose = () => {
    if (navigation.canGoBack()) navigation.goBack()
    else navigation.navigate(paths.Console._self)
  }
  return (
    <View style={ProgressHeaderStyles.root}>
      <View style={ProgressHeaderStyles.subView}>
        <View style={{ width: '8%' }} />
        {sm ? (
          <View style={{ flexDirection: 'row' }}>
            {sectionList.map((k, i) => {
              const r = k.replace(/([a-z])([A-Z])/g, '$1 $2')
              const category = sectionCategory(k)
              return (
                <React.Fragment key={k}>
                  <SectionName
                    name={r.replace(/^./, r[0].toUpperCase())}
                    category={category}
                  />
                  {i !== sectionList.length - 1 && (
                    <ChevronRightDark
                      style={{
                        alignSelf: 'center',
                        marginLeft: 30,
                        marginRight: 30,
                      }}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </View>
        ) : (
          <Text style={ProgressHeaderStyles.mobileTitle}>
            {t('ProjectForm.mobile-title')}
          </Text>
        )}
        <TouchableOpacity onPress={onClose} style={ProgressHeaderStyles.close}>
          <IconCloseSquare width={12} height={12} />
        </TouchableOpacity>
      </View>
      <View
        style={composeStyle(ProgressHeaderStyles.progressBar, {
          width: progressWidth,
        })}
      />
    </View>
  )
}

const SectionNameStyles = StyleSheet.create({
  name: {
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 22,
    color: '#19262F',
  },
  checkContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
})

const ProgressHeaderStyles = StyleSheet.create({
  root: {
    height: 64,
    width: '100%',
    justifyContent: 'center',
    borderBottomColor: '#EEF2F5',
    borderBottomWidth: 1,
  },
  subView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 12,
    alignItems: 'center',
  },
  progressBar: {
    height: 3,
    borderRadius: 3,
    backgroundColor: '#00A0F3',
  },
  mobileTitle: {
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 24,
  },
  close: {
    borderColor: '#E6EBEE',
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    backgroundColor: '#F5F7F8',
  },
})
